import React, { useState } from 'react';
import { auth, database, storage } from '../../firebaseConfig';
import { ref as dbRef, push, set } from 'firebase/database';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import VideoUploader from './VideoUploader';
import './AddVideo.css';

function AddVideo({ categories, onVideoAdded, onCancel }) {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        videoUrl: '',
        categoryId: ''
    });
    const [videoSource, setVideoSource] = useState('url'); // 'url' or 'upload'
    const [uploadedVideo, setUploadedVideo] = useState(null);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [migrationStatus, setMigrationStatus] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleThumbnailChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError('Please select a valid image file for thumbnail');
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError('Thumbnail file size must be less than 5MB');
                return;
            }

            setThumbnailFile(file);
            setError('');

            // Create preview
            const reader = new FileReader();
            reader.onload = () => {
                setThumbnailPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadThumbnail = async (file) => {
        if (!auth.currentUser || !file) return null;

        const fileName = `academy/videos/thumbnails/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, fileName);
        
        try {
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            console.error('Error uploading thumbnail:', error);
            throw new Error('Failed to upload thumbnail');
        }
    };

    const validateVideoUrl = (url) => {
        if (!url) return false;
        
        // YouTube video patterns
        const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)[a-zA-Z0-9_-]{11}/;
        
        // Vimeo video patterns
        const vimeoRegex = /^(https?:\/\/)?(www\.)?(vimeo\.com\/)[0-9]+/;
        
        // General video file patterns
        const videoFileRegex = /\.(mp4|webm|ogg|avi|mov|wmv|flv)(\?.*)?$/i;
        
        return youtubeRegex.test(url) || vimeoRegex.test(url) || videoFileRegex.test(url);
    };

    const handleVideoUploaded = (videoMetadata) => {
        setUploadedVideo(videoMetadata);
        // Auto-fill title if not set
        if (!formData.title) {
            setFormData(prev => ({
                ...prev,
                title: videoMetadata.originalName.replace(/\.[^/.]+$/, '') // Remove file extension
            }));
        }
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!auth.currentUser) {
            setError('You must be logged in to create a video');
            return;
        }

        if (!formData.title.trim()) {
            setError('Video title is required');
            return;
        }

        if (!formData.categoryId) {
            setError('Please select a category');
            return;
        }

        // Validate video source
        if (videoSource === 'url') {
            if (!formData.videoUrl.trim()) {
                setError('Video URL is required');
                return;
            }
            if (!validateVideoUrl(formData.videoUrl)) {
                setError('Please enter a valid video URL (YouTube, Vimeo, or direct video file)');
                return;
            }
        } else if (videoSource === 'upload') {
            if (!uploadedVideo) {
                setError('Please upload a video file');
                return;
            }
        }

        setLoading(true);
        setError('');

        try {
            let thumbnailUrl = null;
            
            // Upload thumbnail if provided
            if (thumbnailFile) {
                thumbnailUrl = await uploadThumbnail(thumbnailFile);
            }

            // Create video data
            const videoData = {
                title: formData.title.trim(),
                description: formData.description.trim(),
                videoUrl: videoSource === 'url' ? formData.videoUrl.trim() : uploadedVideo.downloadURL,
                videoSource: videoSource,
                thumbnailUrl: thumbnailUrl,
                categoryId: formData.categoryId,
                createdBy: auth.currentUser.email,
                createdByUserId: auth.currentUser.uid,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            // Add uploaded video metadata if available
            if (videoSource === 'upload' && uploadedVideo) {
                videoData.videoMetadata = {
                    fileName: uploadedVideo.fileName,
                    originalName: uploadedVideo.originalName,
                    fileSize: uploadedVideo.fileSize,
                    fileType: uploadedVideo.fileType,
                    duration: uploadedVideo.duration,
                    width: uploadedVideo.width,
                    height: uploadedVideo.height,
                    storagePath: uploadedVideo.storagePath
                };
            }

            // Push to public Academy path
            const videosRef = dbRef(database, 'Academy/videos');
            const newVideoRef = push(videosRef);
            await set(newVideoRef, videoData);

            // Create the video object with the generated ID
            const newVideo = {
                id: newVideoRef.key,
                ...videoData
            };

            // Migrate uploaded videos to Bunny CDN in background
            if (videoSource === 'upload') {
                setMigrationStatus('migrating');
                const fns = getFunctions();
                const migrateVideoToBunny = httpsCallable(fns, 'migrateVideoToBunny');
                migrateVideoToBunny({ videoId: newVideoRef.key })
                    .then(() => setMigrationStatus('done'))
                    .catch((err) => {
                        console.warn('Bunny migration deferred:', err.message);
                        setMigrationStatus('deferred');
                    });
            }

            onVideoAdded(newVideo);

        } catch (error) {
            console.error('Error creating video:', error);
            setError('Failed to create video. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const removeThumbnail = () => {
        setThumbnailFile(null);
        setThumbnailPreview(null);
    };

    if (categories.length === 0) {
        return (
            <div className="add-video-container">
                <div className="add-video-header">
                    <h3>Add New Video</h3>
                    <p>You need at least one category before adding videos</p>
                </div>
                <div className="no-categories-message">
                    <p>Please create a category first before adding videos.</p>
                    <button onClick={onCancel} className="cancel-btn">
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    // Show video uploader if upload mode is selected
    if (videoSource === 'upload' && !uploadedVideo) {
        return (
            <VideoUploader
                onVideoUploaded={handleVideoUploaded}
                onCancel={() => setVideoSource('url')}
            />
        );
    }

    return (
        <div className="add-video-container">
            <div className="add-video-header">
                <h3>Add New Learning Video</h3>
                <p>Add a video to your learning library</p>
            </div>

            <form onSubmit={handleSubmit} className="add-video-form">
                {error && <div className="error-message">{error}</div>}

                <div className="form-group">
                    <label htmlFor="title">Video Title *</label>
                    <input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="e.g., How to Close Deals Effectively"
                        maxLength={100}
                        required
                    />
                    <small className="char-count">{formData.title.length}/100</small>
                </div>

                <div className="form-group">
                    <label htmlFor="categoryId">Category *</label>
                    <select
                        id="categoryId"
                        name="categoryId"
                        value={formData.categoryId}
                        onChange={handleInputChange}
                        required
                    >
                        <option value="">Select a category</option>
                        {categories.map(category => (
                            <option key={category.id} value={category.id}>
                                {category.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>Video Source *</label>
                    <div className="video-source-selector">
                        <button
                            type="button"
                            className={`source-btn ${videoSource === 'url' ? 'active' : ''}`}
                            onClick={() => setVideoSource('url')}
                        >
                            🔗 URL/Link
                        </button>
                        <button
                            type="button"
                            className={`source-btn ${videoSource === 'upload' ? 'active' : ''}`}
                            onClick={() => setVideoSource('upload')}
                        >
                            📤 Upload File
                        </button>
                    </div>
                </div>

                {videoSource === 'url' ? (
                    <div className="form-group">
                        <label htmlFor="videoUrl">Video URL *</label>
                        <input
                            type="url"
                            id="videoUrl"
                            name="videoUrl"
                            value={formData.videoUrl}
                            onChange={handleInputChange}
                            placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                            required
                        />
                        <small className="url-help">
                            Supports YouTube, Vimeo, or direct video file URLs
                        </small>
                    </div>
                ) : (
                    <div className="form-group">
                        <label>Uploaded Video</label>
                        {uploadedVideo ? (
                            <div className="uploaded-video-info">
                                <div className="video-preview-small">
                                    <video src={uploadedVideo.downloadURL} controls style={{width: '100%', height: '200px', objectFit: 'cover', borderRadius: '8px'}} />
                                </div>
                                <div className="video-details">
                                    <strong>{uploadedVideo.originalName}</strong>
                                    <span>Size: {(uploadedVideo.fileSize / (1024 * 1024)).toFixed(2)} MB</span>
                                    <span>Duration: {uploadedVideo.duration ? `${Math.round(uploadedVideo.duration)}s` : 'Unknown'}</span>
                                    {uploadedVideo.width && uploadedVideo.height && (
                                        <span>Resolution: {uploadedVideo.width}x{uploadedVideo.height}</span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="change-video-btn"
                                    onClick={() => {
                                        setUploadedVideo(null);
                                        setVideoSource('upload');
                                    }}
                                >
                                    Change Video
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="upload-trigger-btn"
                                onClick={() => setVideoSource('upload')}
                            >
                                Click to Upload Video
                            </button>
                        )}
                    </div>
                )}

                <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Describe what this video teaches..."
                        rows={6}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="thumbnail">Video Thumbnail (Optional)</label>
                    <div className="image-upload-area">
                        {thumbnailPreview ? (
                            <div className="image-preview">
                                <img src={thumbnailPreview} alt="Video thumbnail preview" />
                                <button 
                                    type="button" 
                                    className="remove-image-btn"
                                    onClick={removeThumbnail}
                                >
                                    ✕
                                </button>
                            </div>
                        ) : (
                            <div className="image-upload-placeholder">
                                <div className="upload-icon">🎬</div>
                                <p>Choose a thumbnail for this video</p>
                                <small>JPG, PNG up to 5MB (recommended: 16:9 aspect ratio)</small>
                            </div>
                        )}
                        <input
                            type="file"
                            id="thumbnail"
                            accept="image/*"
                            onChange={handleThumbnailChange}
                            className="image-input"
                        />
                    </div>
                </div>

                <div className="form-actions">
                    <button 
                        type="button" 
                        onClick={onCancel}
                        className="cancel-btn"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button 
                        type="submit" 
                        className="submit-btn"
                        disabled={loading || !formData.title.trim() || !formData.categoryId || 
                                 (videoSource === 'url' && !formData.videoUrl.trim()) ||
                                 (videoSource === 'upload' && !uploadedVideo)}
                    >
                        {loading ? 'Creating...' : 'Create Video'}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default AddVideo;
